import { TOURISM_DESTINATIONS } from "./tourismDestinations";
import { LOCATIONS } from "./locations";
import { APPROVED_CITY_NOTES } from "./approvedCityNotes";
/**
 * Per-city UNIQUE content bank for organic search targeting.
 *
 * Rules (must not be violated):
 *   • Every city gets factually different content. NEVER just replace a
 *     city name in a template — that triggers Google's "doorway page"
 *     spam classification.
 *   • Do NOT claim VVPSS has an office/branch/permanent fleet in a city
 *     other than Satna. Use `pickupNote` phrased as "Pickup service is
 *     available with advance booking."
 *   • Do NOT fabricate addresses, GBP profiles or local phone numbers.
 *   • Routes / distances are existing approximate journey-planning examples;
 *     final fare confirmed on WhatsApp.
 *
 * If a city is not in this file, LocationDetail falls back to generic copy.
 */

import { EXTRA_TAXI_CITY_CONTENT, EXTRA_TEMPO_CITY_CONTENT, CITY_HIRE_DETAILS } from "./localHireContent";

const CENTRAL_HUB_NOTE =
    "Our corporate office is in Satna. All listed services and vehicles are offered in this location. Share your dates and pickup address to confirm your vehicle, pickup time and complete fare.";

// Shared vehicle line-up (unchanged across cities, referenced in copy)
const FLEET_LINE = "Dzire, Etios, Brezza, Ertiga, Kia Carens, Innova Crysta, Bolero, Bolero Neo, Scorpio, Force Trax Cruiser and 13/17/20/21/26-seater Tempo Traveller options. Availability and vehicle configuration are confirmed when booking.";

/**
 * Taxi/Cab city page content.
 * Fields: intro (2-3 sentences), whyUs (bullets), pickupPoints, railwayStations,
 * attractions, routes ({to, km, hours, fare}), faqs, nearbyCities (for internal links),
 * intent ("general" | "pilgrimage" | "railway").
 */
export const TAXI_CITY_CONTENT = {
    ...EXTRA_TAXI_CITY_CONTENT,
    satna: {
        intent: "general",
        heroPitch:
            "Book taxi services in Satna for local stops, railway transfers and outstation journeys. Share your pickup, travel date and return plan to arrange a suitable vehicle with driver.",
        intro: [
            "Satna is a district headquarters in the Vindhya region of Madhya Pradesh, and one of Central India's busiest commercial hubs — home to cement plants, coaching institutes and a large railway junction on the Howrah–Mumbai line.",
            "Compare our AC cars, SUVs and Tempo Travellers for cab booking in Satna. Local hire and outstation fares have different fuel rules; review the selected vehicle and request a complete trip quote.",
        ],
        pickupPoints: [
            "Satna Junction Railway Station",
            "Sindhi Camp / Bus Stand",
            "Rewa Road Bypass",
            "Panna Naka",
            "Semariya Chowk",
            "Bharhut Nagar / Hospital Chowk",
        ],
        railwayStations: [
            { name: "Satna Junction (STA)", note: "Major stop on Mumbai–Howrah route." },
        ],
        attractions: [
            "Bharhut Stupa (Buddhist heritage)",
            "Madhavgarh Fort",
            "Jain temple complex",
            "Ram Van (Pashupatinath)",
            "Venkatesh Temple",
        ],
        routes: [
            { to: "Prayagraj (Allahabad)", km: 165, hours: "3.5 hrs", fare: "Trip-specific quote" },
            { to: "Varanasi", km: 265, hours: "5.5 hrs", fare: "Trip-specific quote" },
            { to: "Khajuraho", km: 120, hours: "2.5 hrs", fare: "Trip-specific quote" },
            { to: "Chitrakoot", km: 78, hours: "1.75 hrs", fare: "Trip-specific quote" },
            { to: "Rewa", km: 50, hours: "1.5 hrs", fare: "Trip-specific quote" },
            { to: "Jabalpur", km: 190, hours: "4 hrs", fare: "Trip-specific quote" },
            { to: "Maihar", km: 40, hours: "1 hr", fare: "Trip-specific quote" },
            { to: "Bhopal", km: 490, hours: "9 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Do you provide 24×7 taxi service in Satna?", a: "You can send a taxi enquiry on WhatsApp at any time. Share your pickup time, route and preferred vehicle; the team confirms availability and dispatch before the booking is final." },
            { q: "Can I hire a cab hourly within Satna city?", a: "Yes. We offer local packages ideal for shopping, sightseeing, weddings and business meetings — see the homepage fare calculator for package details." },
            { q: "Where is your office in Satna?", a: "Our office is in Satna, Madhya Pradesh. Call +91 94257 12055 or use the contact form for exact location and to schedule a visit." },
        ],
        nearbyCities: ["rewa", "maihar", "chitrakoot", "khajuraho"],
        pickupNote: null, // this is the hub
    },

    rewa: {
        intent: "general",
        heroPitch:
            "Arrange Rewa cab booking for railway pickup, city stops or waterfall outings. Share arrival details and the hotel address, with a separate stop and waiting plan for sightseeing.",
        intro: [
            "Rewa, the erstwhile princely capital of the Baghel dynasty, is a fast-growing city in eastern Madhya Pradesh known for the world's first white-tiger breeding centre, imposing Rewa Fort and dramatic waterfalls of the Vindhya escarpment.",
            "VVPSS offers all listed services and vehicle options in Rewa, including local cab booking and outstation taxi hire. Share your station, bus stand, campus or hotel pickup details and travel dates to confirm your vehicle and fare.",
        ],
        pickupPoints: [
            "Rewa Junction Railway Station",
            "Rewa Bus Stand (Chorhata)",
            "Awadhesh Pratap Singh University",
            "Rewa Fort area",
            "Sirmour Chowk",
        ],
        railwayStations: [
            { name: "Rewa Junction (REWA)", note: "Terminal station; direct trains to Delhi, Prayagraj, Indore." },
        ],
        attractions: [
            "Rewa Fort & Palace Museum",
            "Keoti Falls (98 m drop)",
            "Purwa Falls",
            "Govindgarh Fort & Palace",
            "Chachai Falls",
            "Deur Kothar Buddhist Stupas",
        ],
        routes: [
            { to: "Satna", km: 50, hours: "1.5 hrs", fare: "Trip-specific quote" },
            { to: "Prayagraj (Allahabad)", km: 155, hours: "3 hrs", fare: "Trip-specific quote" },
            { to: "Varanasi", km: 235, hours: "5 hrs", fare: "Trip-specific quote" },
            { to: "Khajuraho", km: 180, hours: "4 hrs", fare: "Trip-specific quote" },
            { to: "Jabalpur", km: 240, hours: "5 hrs", fare: "Trip-specific quote" },
            { to: "Chitrakoot", km: 135, hours: "3 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Can I book a taxi from Rewa Junction railway station?", a: "Yes. Railway pickup is offered in Rewa with all listed vehicle options. Share your train number, arrival time, passengers and luggage; confirm vehicle availability and an accessible station meeting point before travel." },
            { q: "How far is Prayagraj from Rewa by cab?", a: "Approximately 155 km, roughly 3 hours by NH-30. We offer one-way drops and round-trip packages." },
            { q: "Can I book all your vehicle options and services in Rewa?", a: "Yes. All listed services and vehicle options are offered in Rewa. Our corporate office is in Satna. Share the pickup, travel dates and itinerary to confirm your vehicle and complete fare." },
        ],
        nearbyCities: ["satna", "maihar", "prayagraj"],
        pickupNote: CENTRAL_HUB_NOTE,
    },

    katni: {
        intent: "general",
        heroPitch:
            "Katni is a key railway junction on the Mumbai–Howrah line. All listed services and vehicle options are offered in Katni, including station pickup and onward trips to Maihar, Satna, Jabalpur and Bandhavgarh.",
        intro: [
            "Katni (locally Mudwara) is a strategically located commercial and railway city in Madhya Pradesh, sitting at the intersection of two major broad-gauge lines and serving as a gateway to Bandhavgarh National Park.",
            "VVPSS provides taxi and outstation cab service in Katni — Katni Junction pickup, city hire, or long drops to Jabalpur, Bhedaghat, Bandhavgarh and beyond. Fleet: {fleet}",
        ].map((s) => s.replace("{fleet}", FLEET_LINE)),
        pickupPoints: [
            "Katni Junction Railway Station",
            "Katni Murwara Railway Station",
            "Katni Bus Stand",
            "Mission Chowk",
            "NKJ (New Katni Junction) area",
        ],
        railwayStations: [
            { name: "Katni Junction (KTE)", note: "Major junction connecting East and West India." },
            { name: "Katni Murwara (KMZ)", note: "Secondary station within Katni." },
        ],
        attractions: [
            "Bahoriband Jain Temple",
            "Vijayraghavgarh Fort",
            "Rupnath Temple (Ashokan edict)",
            "Bilhari Kalchuri ruins",
        ],
        routes: [
            { to: "Maihar", km: 60, hours: "1.25 hrs", fare: "Trip-specific quote" },
            { to: "Satna", km: 100, hours: "2 hrs", fare: "Trip-specific quote" },
            { to: "Jabalpur", km: 95, hours: "2 hrs", fare: "Trip-specific quote" },
            { to: "Bandhavgarh (Umaria)", km: 100, hours: "2.5 hrs", fare: "Trip-specific quote" },
            { to: "Bhedaghat (Marble Rocks)", km: 115, hours: "2.5 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Can you provide a taxi at Katni Junction late at night?", a: "Late-night Katni pickup can be requested in advance. Send the exact station, arrival time and destination; availability and dispatch time are confirmed before booking." },
            { q: "Do you have Bandhavgarh National Park tour packages from Katni?", a: "Yes — Katni to Bandhavgarh is ~100 km. We offer round-trip cab packages with waiting time for safari; final fare depends on vehicle and total km." },
        ],
        nearbyCities: ["maihar", "satna", "jabalpur"],
        pickupNote: CENTRAL_HUB_NOTE,
    },

    chitrakoot: {
        intent: "pilgrimage",
        heroPitch:
            "Plan Chitrakoot car hire around your temple stops, hotel or ashram and accessible parking points. Specify whether your railway transfer starts at Karwi or Manikpur.",
        intro: [
            "Chitrakoot Dham straddles the Madhya Pradesh–Uttar Pradesh border along the Mandakini river and is one of the holiest Ram-linked pilgrimage sites in India — a natural cluster of temples, ghats, forests and hills.",
            "VVPSS offers all listed services and vehicle options in Chitrakoot, including Ram Ghat transfers, temple itineraries and combined Chitrakoot + Khajuraho or Chitrakoot + Ayodhya multi-day tours.",
        ],
        pickupPoints: [
            "Ram Ghat parking",
            "Karwi Railway Station (~11 km)",
            "Bharat Milap Mandir",
            "Sati Anusuiya road head",
            "Kamadgiri Parikrama gate",
        ],
        railwayStations: [
            { name: "Chitrakoot Dham Karwi (CKTD)", note: "Nearest railhead — 11 km from Ram Ghat." },
            { name: "Manikpur Junction (MKP)", note: "Alternate railhead — ~30 km, with more train options." },
        ],
        attractions: [
            "Ram Ghat (evening aarti on Mandakini)",
            "Kamadgiri Parikrama (5 km circular walk)",
            "Hanuman Dhara temple (steep climb, hilltop shrine)",
            "Bharat Milap Mandir",
            "Sati Anusuiya Ashram",
            "Gupt-Godavari caves",
            "Sphatik Shila (Ram–Sita foot impression)",
        ],
        routes: [
            { to: "Satna", km: 78, hours: "1.75 hrs", fare: "Trip-specific quote" },
            { to: "Karwi", km: 11, hours: "25 min", fare: "Trip-specific quote" },
            { to: "Khajuraho", km: 175, hours: "4 hrs", fare: "Trip-specific quote" },
            { to: "Prayagraj (Allahabad)", km: 130, hours: "3 hrs", fare: "Trip-specific quote" },
            { to: "Ayodhya", km: 300, hours: "6.5 hrs", fare: "Trip-specific quote" },
            { to: "Varanasi", km: 235, hours: "5 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Do you offer full-day Chitrakoot yatra packages?", a: "Yes — our full-day package covers Ram Ghat, Kamadgiri Parikrama, Hanuman Dhara, Sati Anusuiya, Gupt-Godavari and Sphatik Shila. Vehicle waits between darshans; get the final quote on WhatsApp." },
            { q: "Can I combine Chitrakoot with Ayodhya or Khajuraho?", a: "Yes — Chitrakoot pairs naturally with Ayodhya (2-day yatra) or Khajuraho (heritage + spirituality). Ask for a multi-day quote showing the vehicle fare and extra toll, parking, border, night and driver meal charges." },
            { q: "Which is the closest railway station to Chitrakoot?", a: "Chitrakoot Dham Karwi station is ~11 km from Ram Ghat. Manikpur Junction (~30 km) is an alternative with more train options. We provide pickup from either." },
        ],
        nearbyCities: ["karwi", "satna", "prayagraj"],
        pickupNote: CENTRAL_HUB_NOTE,
    },

    karwi: {
        intent: "railway",
        heroPitch:
            "Arrange taxi pickup at Chitrakoot Dham Karwi railway station for your hotel, ashram or onward journey. Share the station name on your ticket and the full passenger and luggage count.",
        intro: [
            "Karwi is a small town in Uttar Pradesh's Chitrakoot district and the railway gateway for the Chitrakoot pilgrimage circuit. Most travellers alight here and continue by cab to Ram Ghat (11 km).",
            "Request Karwi station pickup, an accessible Chitrakoot temple or ashram drop, or outstation travel to Satna, Prayagraj or Khajuraho. Agree the meeting point, stops and dispatch plan in advance.",
        ],
        pickupPoints: [
            "Chitrakoot Dham Karwi Railway Station (main exit)",
            "Karwi Bus Stand",
            "Karwi Bazaar",
            "Ram Ghat road head",
        ],
        railwayStations: [
            { name: "Chitrakoot Dham Karwi (CKTD)", note: "Primary railhead for Chitrakoot pilgrims. Direct trains from Delhi, Prayagraj, Varanasi, Jabalpur." },
        ],
        attractions: [
            "Ram Ghat, Chitrakoot (11 km — most common drop)",
            "Kamadgiri Parikrama",
            "Karwi local temples",
            "Hanuman Dhara temple",
        ],
        routes: [
            { to: "Ram Ghat, Chitrakoot", km: 11, hours: "25 min", fare: "Trip-specific quote" },
            { to: "Satna", km: 90, hours: "2 hrs", fare: "Trip-specific quote" },
            { to: "Prayagraj (Allahabad)", km: 125, hours: "2.75 hrs", fare: "Trip-specific quote" },
            { to: "Khajuraho", km: 185, hours: "4 hrs", fare: "Trip-specific quote" },
            { to: "Varanasi", km: 240, hours: "5 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Can I book a taxi from Karwi railway station to Chitrakoot Ram Ghat?", a: "Yes, request the station transfer with your train details, group contact and final Ram Ghat meeting point. The driver and accessible drop location are confirmed with your booking." },
            { q: "How far in advance should I book Karwi station pickup?", a: "Send your train details as early as possible. All listed vehicle options are offered in Karwi; the team confirms availability for your travel date and agreed pickup time." },
            { q: "Do you provide multi-day Chitrakoot yatra starting from Karwi station?", a: "Yes — many pilgrims arrive at Karwi, drop luggage at their Chitrakoot dharamshala, and hire our cab for the full day's darshan. See /taxi-service-chitrakoot for the full yatra plan." },
        ],
        nearbyCities: ["chitrakoot", "prayagraj", "satna"],
        pickupNote: CENTRAL_HUB_NOTE,
    },

    panna: {
        intent: "general",
        heroPitch:
            "Arrange Panna car hire for town travel, Khajuraho transfers and tiger reserve gate pickup or drop. Road transport is separate from safari permits and safari vehicles.",
        intro: [
            "Panna town in Madhya Pradesh's Bundelkhand region is the gateway to Panna Tiger Reserve — one of India's most successful tiger repopulation projects — and is also known for its ancient diamond mines and Pandav-era waterfalls.",
            "VVPSS provides taxi service in Panna covering Panna town, Madla safari gate, Hinouta gate and combined day tours with Khajuraho (only 45 km away). Park permits and safari vehicles must be arranged separately.",
        ],
        pickupPoints: [
            "Panna Bus Stand",
            "Madla safari gate (main entry)",
            "Hinouta safari gate",
            "Panna town centre",
            "Panna State Museum area",
        ],
        railwayStations: [
            { name: "Satna Junction (STA)", note: "Nearest major railhead — 75 km." },
            { name: "Khajuraho Station (KURJ)", note: "Nearest secondary — 45 km." },
        ],
        attractions: [
            "Panna Tiger Reserve (Madla / Hinouta gates)",
            "Pandav Falls & caves",
            "Diamond Mines (Majhgawan)",
            "Ken Gharial Sanctuary",
            "Raneh Falls (canyon of Ken river)",
            "Baldeo-ji Temple",
        ],
        routes: [
            { to: "Khajuraho", km: 45, hours: "1 hr", fare: "Trip-specific quote" },
            { to: "Satna", km: 75, hours: "1.75 hrs", fare: "Trip-specific quote" },
            { to: "Chhatarpur", km: 85, hours: "2 hrs", fare: "Trip-specific quote" },
            { to: "Chitrakoot", km: 145, hours: "3.25 hrs", fare: "Trip-specific quote" },
            { to: "Jabalpur", km: 250, hours: "5.5 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Can you arrange a Panna Tiger Reserve safari from Khajuraho?", a: "Yes — cab drop from Khajuraho to Madla gate (~40 km) plus wait during the 3-4 hr jungle safari, then return. Jungle-safari jeep and permit must be booked separately with the forest department." },
            { q: "Is Panna open during monsoon?", a: "Park and attraction access can change by season and local conditions. Check the current forest-department booking information for your date before arranging the road transfer; a taxi booking does not include safari entry." },
        ],
        nearbyCities: ["khajuraho", "satna", "chhatarpur"],
        pickupNote: CENTRAL_HUB_NOTE,
    },

    maihar: {
        intent: "general",
        heroPitch:
            "Book Maihar taxi service for Maa Sharda Devi temple visits, railway pickup and onward travel. Agree parking, waiting and return pickup, and include the train connection in your itinerary.",
        intro: [
            "Maihar in Madhya Pradesh is known for the hilltop Maa Sharda Devi temple. For a temple visit, plan the vehicle meeting point, darshan time and return journey separately.",
            "VVPSS runs taxi service to Maihar from Satna (~40 km, 1 hour) and connects Maihar with Katni, Rewa, Jabalpur and Khajuraho. Popular as a half-day pilgrimage from Satna or a stop on longer Vindhya-region tours.",
        ],
        pickupPoints: [
            "Maihar Junction Railway Station",
            "Maihar Bus Stand",
            "Trikut Hill temple base (ropeway station)",
            "Aalha-Udal Akhada",
        ],
        railwayStations: [
            { name: "Maihar Junction (MYR)", note: "Direct trains from Mumbai, Kolkata, Delhi, Prayagraj." },
        ],
        attractions: [
            "Maa Sharda Devi Temple (Trikut Hill)",
            "Ropeway to hilltop shrine",
            "Aalha-Udal Akhada",
            "Bada Akhada Mandir",
            "Icchapurti Temple",
            "Golamath Temple (10th century)",
        ],
        routes: [
            { to: "Satna", km: 40, hours: "1 hr", fare: "Trip-specific quote" },
            { to: "Katni", km: 60, hours: "1.25 hrs", fare: "Trip-specific quote" },
            { to: "Rewa", km: 110, hours: "2.25 hrs", fare: "Trip-specific quote" },
            { to: "Jabalpur", km: 160, hours: "3.5 hrs", fare: "Trip-specific quote" },
            { to: "Khajuraho", km: 160, hours: "3.5 hrs", fare: "Trip-specific quote" },
        ],
        faqs: [
            { q: "Can I do a same-day Satna–Maihar temple round trip?", a: "Yes — this is our most-booked package. Depart early morning, allow 2-3 hours at the temple (including ropeway and darshan), return by afternoon. Total ~5-6 hours." },
            { q: "Do you provide taxi at Maihar Junction railway station?", a: "Yes — with advance booking, driver meets you at the station exit and drives you directly to the ropeway station or temple base." },
        ],
        nearbyCities: ["satna", "katni", "rewa"],
        pickupNote: CENTRAL_HUB_NOTE,
    },
};

/**
 * Tempo Traveller city page content.
 * Each city has UNIQUE use-case emphasis (wedding, pilgrimage tour, corporate,
 * airport shuttle, etc.) — not just city-name substitution.
 */
export const TEMPO_CITY_CONTENT = {
    ...EXTRA_TEMPO_CITY_CONTENT,
    satna: {
        intent: "general",
        heroPitch:
            "Compare Tempo Traveller on hire in Satna for family journeys, wedding guests and multi-day tours. Send group size, luggage, stops and travel dates to confirm availability and dispatch.",
        useCases: [
            { title: "Wedding baraat & guest shuttle", desc: "Full-day hire with driver — decorated on request. Popular for Satna city + Rewa Road banquet halls." },
            { title: "Family pilgrimage circuit", desc: "Plan Chitrakoot, Khajuraho, Maihar or Bageshwar Dham stops with overnight stays and luggage requirements. Compare the AC pushback seating options before booking." },
            { title: "Corporate offsite / employee shuttle", desc: "Share reporting times, employee or guest count, pickup sequence and return timing for a trip-specific group transport quote." },
            { title: "School / college educational trips", desc: "17/20/26 seater options for group day-outs to Bharhut, Chitrakoot or Khajuraho." },
        ],
        popularRoutes: [
            { from: "Satna", to: "Khajuraho + Panna", km: "~250 km round-trip", ideal: "17/20-seater family day tour" },
            { from: "Satna", to: "Chitrakoot yatra", km: "~220 km round-trip", ideal: "13/17-seater pilgrimage" },
            { from: "Satna", to: "Bageshwar Dham + Khajuraho", km: "~300 km round-trip", ideal: "20-seater full-day yatra" },
        ],
        pickupPoints: ["Satna Junction Railway Station", "Sindhi Camp Bus Stand", "Hotel / residence within 15 km"],
        faqs: [
            { q: "Which tempo traveller is best for a Satna–Khajuraho–Panna day trip?", a: "For a family of 8-12 the 17-seater with push-back seats is ideal — comfortable for the 250 km round-trip. For 15-19 passengers, consider the 20-seater luxury variant (19 passengers plus driver)." },
            { q: "Can I hire a tempo traveller for a wedding in Satna?", a: "Yes — we do full-day baraat + guest shuttle bookings. Decoration and matching cars can be arranged on request; ask for a wedding-package quote on WhatsApp." },
        ],
        pickupNote: null,
        nearbyCities: ["rewa", "maihar", "chitrakoot"],
    },

    rewa: {
        intent: "general",
        heroPitch:
            "Rewa families use our tempo travellers most for Chitrakoot + Prayagraj pilgrimages, and for wedding-guest shuttles between Rewa banquet halls and hotels.",
        useCases: [
            { title: "Rewa → Prayagraj Sangam yatra", desc: "One-day Sangam snan or two-day Kumbh-style itinerary — 17/20-seater with pushback seats." },
            { title: "Wedding transport in Rewa", desc: "Plan wedding-guest transfers between venues and hotels. List pickup times, passengers and luggage for each leg." },
            { title: "Family reunions / picnics", desc: "Day-trips to Keoti Falls, Purwa Falls or Govindgarh Palace in a 13/17-seater." },
            { title: "Corporate group travel", desc: "APS University conferences, offsite meetings — group rates with GST invoice." },
        ],
        popularRoutes: [
            { from: "Rewa", to: "Prayagraj Sangam", km: "~310 km round-trip", ideal: "17/20-seater one-day yatra" },
            { from: "Rewa", to: "Chitrakoot", km: "~270 km round-trip", ideal: "17-seater pilgrimage" },
            { from: "Rewa", to: "Khajuraho + Panna", km: "~360 km round-trip", ideal: "20-seater group tour" },
        ],
        pickupPoints: ["Rewa Junction", "Chorhata Bus Stand", "APS University", "Any hotel / residence"],
        faqs: [
            { q: "What tempo traveller sizes are available for a Rewa wedding?", a: "13, 17, 20 and 26 seater — all AC. For a typical mid-sized wedding we recommend combining a 20-seater for family + a 26-seater for guests." },
            { q: "Can I book a one-way tempo from Rewa to Prayagraj?", a: "Yes — one-way and round-trip both offered. One-way is more economical for the outbound leg only; round-trip is better if you need the vehicle in Prayagraj." },
        ],
        pickupNote: CENTRAL_HUB_NOTE,
        nearbyCities: ["satna", "prayagraj"],
    },

    katni: {
        intent: "general",
        heroPitch:
            "Katni's location makes it a natural staging point for Bandhavgarh, Bhedaghat and Jabalpur group trips. Our tempo travellers pick up at Katni Junction and continue onward.",
        useCases: [
            { title: "Bandhavgarh National Park group safari trip", desc: "Katni to Umaria/Tala transfer + return; safari jeep booked separately." },
            { title: "Bhedaghat + Dhuandhar Falls day tour", desc: "Popular Katni round-trip for families and photo enthusiasts." },
            { title: "Wedding-guest transport in Katni", desc: "Multiple pickups at Katni Junction + Murwara + hotels." },
            { title: "Jain community pilgrimage", desc: "Bahoriband + regional Jain temples in a chartered tempo traveller." },
        ],
        popularRoutes: [
            { from: "Katni", to: "Bandhavgarh (Tala)", km: "~200 km round-trip", ideal: "17-seater safari trip" },
            { from: "Katni", to: "Bhedaghat + Jabalpur", km: "~220 km round-trip", ideal: "17/20-seater day tour" },
            { from: "Katni", to: "Maihar + Satna", km: "~200 km round-trip", ideal: "13-seater darshan trip" },
        ],
        pickupPoints: ["Katni Junction (KTE)", "Katni Murwara (KMZ)", "Bus stand", "Mission Chowk"],
        faqs: [
            { q: "Can I hire a 20-seater tempo from Katni for Bandhavgarh?", a: "Yes — with advance booking. Katni to Tala gate is ~100 km one-way. We recommend two overnight in Bandhavgarh + jeep safari booked separately at the forest gate." },
            { q: "What is the largest tempo traveller you offer?", a: "The 26-seater has 25 passenger seats plus the driver, AC, music and pushback seats. Share luggage requirements and Katni pickup details before confirming availability." },
        ],
        pickupNote: CENTRAL_HUB_NOTE,
        nearbyCities: ["maihar", "satna", "jabalpur"],
    },

    chitrakoot: {
        intent: "pilgrimage",
        heroPitch:
            "Arrange Tempo Traveller rental in Chitrakoot for temple visits, ashram transfers and multi-day journeys. List the parking points, group size and overnight stops when requesting hire.",
        useCases: [
            { title: "Full-day Chitrakoot parikrama", desc: "Ram Ghat → Kamadgiri → Hanuman Dhara → Sati Anusuiya → Gupt-Godavari → Sphatik Shila. Vehicle waits between each darshan." },
            { title: "Chitrakoot + Ayodhya 3-day yatra", desc: "Family/community group yatra with driver stay; 20/26-seater ideal." },
            { title: "Chitrakoot + Khajuraho combo tour", desc: "Spiritual + heritage 2-day itinerary in a 17-seater." },
            { title: "Ashram / community bhandara transport", desc: "Bulk-guest transfer between ashrams and Ram Ghat in a 20/26-seater." },
        ],
        popularRoutes: [
            { from: "Chitrakoot", to: "Ayodhya", km: "~600 km round-trip", ideal: "26-seater community yatra" },
            { from: "Chitrakoot", to: "Khajuraho", km: "~350 km round-trip", ideal: "20-seater heritage combo" },
            { from: "Chitrakoot", to: "Prayagraj Sangam", km: "~260 km round-trip", ideal: "17-seater same-day snan" },
        ],
        pickupPoints: ["Ram Ghat parking", "Karwi Railway Station", "Kamadgiri gate", "Sati Anusuiya road head"],
        faqs: [
            { q: "Do you handle multi-day pilgrimage tempo hire for large families?", a: "You can request a multi-day Chitrakoot, Ayodhya or Prayagraj itinerary. Outstation rates include fuel with minimum billing of 300 KM per day; toll, parking, border, night charges and driver meals are extra." },
            { q: "Is there parking near Ram Ghat for a 26-seater tempo?", a: "Ask the team to confirm current permitted parking and a road-accessible meeting point for your travel date. Share the selected vehicle, group size and walking needs; agree the drop, waiting and return pickup before booking." },
        ],
        pickupNote: CENTRAL_HUB_NOTE,
        nearbyCities: ["karwi", "satna", "prayagraj"],
    },

    karwi: {
        intent: "railway",
        heroPitch:
            "Book a Tempo Traveller pickup at Chitrakoot Dham Karwi station in advance. Share the train details, passenger count and ashram or hotel address to confirm an accessible meeting point.",
        useCases: [
            { title: "Group railway pickup at Karwi Junction", desc: "13/17/20/26-seater meets your train — direct drop to Ram Ghat / ashram / dharamshala." },
            { title: "Onward outstation from Karwi", desc: "One-way group drop to Satna, Prayagraj, Varanasi or Ayodhya." },
            { title: "Chitrakoot yatra with return to Karwi station", desc: "Full-day parikrama + timely drop back for return train." },
        ],
        popularRoutes: [
            { from: "Karwi", to: "Chitrakoot Ram Ghat", km: "~22 km round-trip", ideal: "Any seater — quick transfer" },
            { from: "Karwi", to: "Prayagraj Sangam", km: "~250 km round-trip", ideal: "17-seater snan yatra" },
            { from: "Karwi", to: "Ayodhya", km: "~600 km round-trip", ideal: "26-seater multi-day yatra" },
        ],
        pickupPoints: ["Chitrakoot Dham Karwi station exit", "Karwi Bus Stand"],
        faqs: [
            { q: "How large a group can you pick up at Karwi railway station?", a: "The largest option seats 25 passengers plus the driver. For a larger party, request multiple vehicles and share passenger and luggage totals; availability and dispatch must be confirmed before booking." },
            { q: "Can you drop us at our ashram after Karwi station pickup?", a: "Yes — provide the ashram/dharamshala name & Google-pin at booking; driver will drop directly at the gate with luggage assistance." },
        ],
        pickupNote: CENTRAL_HUB_NOTE,
        nearbyCities: ["chitrakoot", "satna", "prayagraj"],
    },

    panna: {
        intent: "general",
        heroPitch:
            "Panna tempo groups are dominated by Khajuraho + Panna tiger reserve combined tours and wildlife-photography clubs. Advance booking essential during safari season.",
        useCases: [
            { title: "Khajuraho + Panna tiger safari combo", desc: "17/20 seater — heritage in the morning, safari transfer in the afternoon." },
            { title: "Panna diamond mines + Pandav Falls day-tour", desc: "13-seater ideal for photographers and history clubs." },
            { title: "Multi-family wildlife holiday", desc: "Compare the 20 and 26-seater options for family groups. Share the number and size of bags to confirm luggage space before booking." },
        ],
        popularRoutes: [
            { from: "Panna", to: "Khajuraho + Raneh Falls", km: "~120 km round-trip", ideal: "17-seater day tour" },
            { from: "Panna", to: "Satna + Bharhut", km: "~180 km round-trip", ideal: "13/17-seater heritage circuit" },
            { from: "Panna", to: "Jabalpur + Bhedaghat", km: "~500 km round-trip", ideal: "20-seater 2-day tour" },
        ],
        pickupPoints: ["Panna Bus Stand", "Madla safari gate", "Panna town centre", "Khajuraho Airport (45 km)"],
        faqs: [
            { q: "Best tempo size for a family of 15 doing Khajuraho + Panna?", a: "17-seater with pushback seats — comfortable for the 120 km loop with kids and elderly. Ideal for a single-day heritage + wildlife combo." },
            { q: "Is Panna safari possible in monsoon?", a: "Check current forest-department access and permit availability for your travel date before booking a road transfer. Safari entry and safari vehicles are separate, and seasonal access must not be assumed." },
        ],
        pickupNote: CENTRAL_HUB_NOTE,
        nearbyCities: ["khajuraho", "satna", "chhatarpur"],
    },

    maihar: {
        intent: "general",
        heroPitch:
            "Arrange Tempo Traveller on rental for a Maihar temple visit or group railway transfer. Confirm parking, waiting time and the onward or return journey, especially for a busy festival date.",
        useCases: [
            { title: "Navratri Sharda Devi darshan yatra", desc: "Share your preferred reporting time, temple parking meeting point and return schedule. Temple entry and ropeway arrangements are separate from vehicle hire." },
            { title: "Maihar + Bageshwar Dham combo", desc: "Two-shakti darshan combo in a 20-seater." },
            { title: "Family darshan + Aalha Akhada visit", desc: "13/17-seater half-day trip from Satna." },
        ],
        popularRoutes: [
            { from: "Maihar", to: "Satna", km: "~80 km round-trip", ideal: "Any seater — quick darshan" },
            { from: "Maihar", to: "Bageshwar Dham", km: "~220 km round-trip", ideal: "20-seater shakti combo" },
            { from: "Maihar", to: "Khajuraho", km: "~320 km round-trip", ideal: "17-seater heritage + darshan" },
        ],
        pickupPoints: ["Maihar Junction (MYR)", "Temple base ropeway station", "Maihar Bus Stand"],
        faqs: [
            { q: "How early should I book a 26-seater tempo for Navratri Maihar darshan?", a: "Send your dates and group size early for a festival enquiry. Confirm vehicle availability, the complete fare, payment requirements and cancellation terms before paying a token." },
            { q: "Does the tempo wait during the ropeway darshan at Maihar?", a: "Include darshan waiting and the agreed parking point in your itinerary. Local hire covers 12 hours; extra duty is charged at the selected vehicle’s hourly rate. Confirm the return pickup and any applicable parking charges." },
        ],
        pickupNote: CENTRAL_HUB_NOTE,
        nearbyCities: ["satna", "katni"],
    },
};

// Destination-specific trip planning; fleet rates remain sourced from the catalog.
for (const destination of TOURISM_DESTINATIONS) {
 const { slug, name, description, pickup, note, taxiIntro, tempoUseCases, nearbyCities, attractions, intent } = destination;
 TAXI_CITY_CONTENT[slug] = {
  heroPitch: description, intro: taxiIntro, pickupPoints: [pickup], attractions, nearbyCities,
  ...(intent === "railway" ? { railwayStations: [{ name: "Rani Kamlapati Railway Station, Bhopal", note: "Send the station name on your ticket, train details and agreed exit or parking meeting point." }] } : {}),
  faqs: [
   { q: `What should I share for ${name} taxi or car hire?`, a: note },
   { q: `Can I request a car or Tempo Traveller for ${name}?`, a: "Compare seating and published rates, then share your travel dates, passengers, luggage and full itinerary. Availability depends on the route, dates and a suitable vehicle. Road hire covers permitted, road-accessible parts of the itinerary, including agreed pickup points or roadheads. Confirm vehicle suitability, pickup arrangements and the complete fare before booking." }
  ]
 };
 TEMPO_CITY_CONTENT[slug] = {
  intent, heroPitch: `Arrange Tempo Traveller hire for ${name}. ${note}`,
  useCases: tempoUseCases, popularRoutes: [], pickupPoints: [pickup], nearbyCities,
  faqs: [
   { q: `How should our group plan a ${name} pickup?`, a: note },
   { q: `Which Tempo Traveller sizes can I request for ${name}?`, a: "Choose from 13, 17, 20, 21 and 26 seats including the driver: 12, 16, 19, 20 and 25 passenger seats respectively. All have AC, music and pushback seats. Share luggage and the pickup access details to confirm a suitable available vehicle." },
   { q: "Does the outstation rate include fuel?", a: "Yes. The vehicle's per-KM rate includes fuel, with minimum billing of 300 KM per day. Toll, parking, border charges, applicable night charges and driver meal are extra. Confirm the full route and final quote before booking." }
  ]
 };
}

// Only approved locations have published city pages.
const approved = new Set(LOCATIONS.map((loc) => loc.slug));
for (const bank of [TAXI_CITY_CONTENT, TEMPO_CITY_CONTENT]) {
 for (const key of Object.keys(bank)) if (!approved.has(key)) delete bank[key];
}
for (const loc of LOCATIONS) {
 const details = CITY_HIRE_DETAILS[loc.slug];
 const note = APPROVED_CITY_NOTES[loc.slug];
 const taxi = TAXI_CITY_CONTENT[loc.slug] || {};
 const hubNote = loc.slug === "satna" ? null : CENTRAL_HUB_NOTE;
 TAXI_CITY_CONTENT[loc.slug] = {
  ...taxi,
  heroPitch: taxi.heroPitch || details.description,
  pickupNote: hubNote,
  intro: taxi.intro || [details.description, note],
  pickupPoints: taxi.pickupPoints || [details.pickup],
  faqs: taxi.faqs || [
   {q: "What pickup details are needed for cab booking in " + loc.name + "?", a: note},
   {q: "Can I arrange car hire or a Tempo Traveller in " + loc.name + "?", a: "Compare the vehicles and charges on this page. Send your travel date, passengers, bags and complete itinerary; availability, dispatch and the final fare are confirmed before booking."}
  ],
 };
 if (!TEMPO_CITY_CONTENT[loc.slug]) TEMPO_CITY_CONTENT[loc.slug] = {
  heroPitch: "Plan Tempo Traveller hire in " + loc.name + ". " + note,
  pickupNote: hubNote,
  useCases: [
   {title: "Your " + loc.name + " pickup plan", desc: note},
   {title: "Choose seating and luggage space", desc: "Our 13, 17, 20, 21 and 26-seater options carry 12, 16, 19, 20 and 25 passengers respectively, plus the driver. Share bags and accessibility needs before choosing."}
  ],
  popularRoutes: [],
  pickupPoints: [details.pickup], nearbyCities: [],
  faqs: [
   {q: "How do I arrange group pickup in " + loc.name + "?", a: note + " Send the passenger count and travel dates to confirm a suitable available vehicle."},
   {q: "What is included in a Tempo Traveller outstation rental?", a: "The vehicle’s per-KM rate includes fuel with minimum billing of 300 KM per day. Toll, parking, border charges, night charges and driver meals are extra. Confirm the total billable route before booking."}
  ]
 };
 TEMPO_CITY_CONTENT[loc.slug].pickupNote = hubNote;
}
// Connect established city pages to the newly requested sightseeing and transfer areas.
const RELATED_DESTINATIONS = {
 rewa: ["sidhi", "singrauli"],
 mauganj: ["sidhi", "singrauli"],
 bhopal: ["rani-kamlapati-railway-station", "bhimbetka", "sanchi", "satpura"],
 jabalpur: ["bhedaghat", "kanha"],
 indore: ["ujjain", "mandu", "maheshwar"],
 khajuraho: ["orchha"],
 gwalior: ["chanderi", "orchha"],
};
for (const [slug, related] of Object.entries(RELATED_DESTINATIONS)) {
 for (const bank of [TAXI_CITY_CONTENT, TEMPO_CITY_CONTENT]) {
  bank[slug].nearbyCities = [...new Set([...(bank[slug].nearbyCities || []), ...related])];
 }
}
// Cities for which we have dedicated Tempo Traveller landing pages.
export const TEMPO_CITY_SLUGS = Object.keys(TEMPO_CITY_CONTENT);
// Cities for which we have enriched Taxi content.
export const TAXI_CITY_SLUGS = Object.keys(TAXI_CITY_CONTENT);
